/**
 * VoiceStatusDot
 * Tiny traffic-light dot that polls /api/whatsapp/voice-health every 60 s.
 * Same pattern as WhatsAppStatusDot — shows 3 states: green / yellow / red.
 * Clicking opens a mini panel with TTS details.
 */
import React, { useEffect, useRef, useState } from "react";

interface VoiceHealth {
  status: "green" | "yellow" | "red";
  color: string;
  label: string;
  detail?: string;
  voices_configured?: number;
  skip_languages?: string[];
  last_tts_age_s?: number | null;
}

const POLL_INTERVAL_MS = 60_000;

function fmtAge(seconds: number | null | undefined): string {
  if (seconds == null) return "—";
  if (seconds < 60)   return `${Math.round(seconds)}s ago`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m ago`;
  return `${Math.round(seconds / 3600)}h ago`;
}

const VoiceStatusDot: React.FC = () => {
  const [health, setHealth] = useState<VoiceHealth | null>(null);
  const [open, setOpen]     = useState(false);
  const panelRef            = useRef<HTMLDivElement>(null);

  const fetchHealth = async () => {
    try {
      const r = await fetch("/api/whatsapp/voice-health");
      if (r.ok) setHealth(await r.json());
    } catch {
      setHealth({ status: "red", color: "#ef4444", label: "Unreachable" });
    }
  };

  useEffect(() => {
    fetchHealth();
    const id = setInterval(fetchHealth, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const color      = health?.color  ?? "#6b7280";
  const label      = health?.label  ?? "Checking…";
  const status     = health?.status ?? "yellow";

  const pulseClass = status === "green"  ? "wa-dot-pulse-green"
                   : status === "yellow" ? "wa-dot-pulse-yellow"
                   : "wa-dot-pulse-red";

  return (
    <div className="wa-status-wrap" ref={panelRef}>
      {/* ── Trigger dot ── */}
      <button
        className="wa-status-btn"
        onClick={() => setOpen(o => !o)}
        title={`Voice AI: ${label}`}
        aria-label="Voice AI status"
      >
        <span className={`wa-dot ${pulseClass}`} style={{ background: color }} />
        <span className="wa-dot-label">🎙️</span>
      </button>

      {/* ── Detail panel ── */}
      {open && (
        <div className="wa-panel">
          <div className="wa-panel-header">
            <span className="wa-panel-icon">🎙️</span>
            <span className="wa-panel-title">Veridian ft. ElevenLabs</span>
            <button className="wa-panel-close" onClick={() => setOpen(false)}>✕</button>
          </div>

          <div className="wa-panel-row">
            <span>Status</span>
            <span style={{ color, fontWeight: 700 }}>{label}</span>
          </div>

          <div className="wa-panel-row">
            <span>Voices configured</span>
            <span>{health?.voices_configured ?? "—"} languages</span>
          </div>

          <div className="wa-panel-row">
            <span>Last TTS call</span>
            <span>{fmtAge(health?.last_tts_age_s)}</span>
          </div>

          {health?.skip_languages && health.skip_languages.length > 0 && (
            <div className="wa-panel-row">
              <span>Text-only langs</span>
              <span>{health.skip_languages.join(", ")}</span>
            </div>
          )}

          {health?.detail && (
            <div className="wa-panel-detail">{health.detail}</div>
          )}

          <div className="wa-panel-actions">
            <button
              className="wa-panel-refresh"
              onClick={fetchHealth}
              title="Refresh status now"
            >
              🔄 Refresh
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default VoiceStatusDot;
